import { Box, Heading, Input, SimpleGrid, Text, VStack } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import type { ChangeEvent, ReactNode } from "react"
import { useEffect } from "react"

import { QuizzesService } from "@/client"
import { ProgramSelector } from "@/components/Programs/ProgramSelector"
import type { ProgramOption } from "@/components/Programs/ProgramSelector"
import { Checkbox } from "@/components/ui/checkbox"
import { Field } from "@/components/ui/field"
import { Radio, RadioGroup } from "@/components/ui/radio"
import type { QuizSettings } from "./QuizBuilder/QuizBuilderContext"
import type { QuizTemplateSettingsForBuilder } from "./QuizBuilder/QuizBuilderContext"
import { QuizTemplateControls } from "./QuizTemplateControls"
import { TagInput } from "./TagInput"

interface QuizSettingsFormProps {
  settings: QuizSettings
  onSettingChange: <K extends keyof QuizSettings>(
    key: K,
    value: QuizSettings[K],
  ) => void
  selectedPrograms: ProgramOption[]
  onProgramsChange: (programs: ProgramOption[]) => void
  buildTemplateSettingsPayload?: () => QuizTemplateSettingsForBuilder
  onApplyTemplateSettings?: (settings: QuizTemplateSettingsForBuilder) => void
}

export function QuizSettingsForm({
  settings,
  onSettingChange,
  selectedPrograms,
  onProgramsChange,
  buildTemplateSettingsPayload,
  onApplyTemplateSettings,
}: QuizSettingsFormProps) {
  // Auto-switch rationale mode when changing to scheduled results
  useEffect(() => {
    if (
      settings.show_result_mode === "scheduled" &&
      settings.show_rationale_mode === "after_each"
    ) {
      onSettingChange("show_rationale_mode", "after_all")
    }
  }, [onSettingChange, settings.show_rationale_mode, settings.show_result_mode])

  const { data: recentTags = [] } = useQuery({
    queryKey: ["quiz-tags"],
    queryFn: () => QuizzesService.listQuizTags(),
  })

  const isScheduled = settings.show_result_mode === "scheduled"
  // Gates both show_peer_performance and allow_rationale_without_attempt:
  // neither has any effect once rationales are hidden entirely.
  const isRationaleDependentSettingDisabled =
    settings.show_rationale_mode === "never"

  return (
    <VStack gap={4} align="stretch">
      <SettingsSection title="Quiz basics">
        <Field label="Quiz name *">
          <Input
            value={settings.name}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onSettingChange("name", event.target.value)
            }
            placeholder="e.g. Algebra Midterm"
          />
        </Field>
      </SettingsSection>

      <SimpleGrid columns={{ base: 1, xl: 2 }} gap={4} alignItems="start">
        <VStack gap={4} align="stretch">
          <SettingsSection
            title="Availability & timing"
            description="Control when students can see this quiz and how long they have."
          >
            <Box role="group" aria-label="Availability dates">
              <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                <Field
                  label="Publish date"
                  helperText="Blank makes the quiz visible immediately. Times use your local timezone."
                >
                  <Input
                    type="datetime-local"
                    value={settings.publish_date ?? ""}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      onSettingChange(
                        "publish_date",
                        event.target.value || null,
                      )
                    }
                  />
                </Field>
                <Field label="Cutoff date">
                  <Input
                    type="datetime-local"
                    value={settings.cutoff_date ?? ""}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      onSettingChange("cutoff_date", event.target.value || null)
                    }
                  />
                </Field>
              </SimpleGrid>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 2 }} gap={3} alignItems="start">
              <Field
                label="Time limit (minutes)"
                helperText="Leave empty for no time limit."
              >
                <Input
                  type="number"
                  min={1}
                  value={settings.time_limit_minutes ?? ""}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    onSettingChange(
                      "time_limit_minutes",
                      event.target.value ? Number(event.target.value) : null,
                    )
                  }
                />
              </Field>
              <Box role="group" aria-label="Retakes">
                <Text fontSize="sm" fontWeight="medium" mb={1.5}>
                  Retakes
                </Text>
                <Box minH="40px" display="flex" alignItems="center">
                  <Checkbox
                    checked={settings.allow_retake ?? false}
                    onCheckedChange={({ checked }) =>
                      onSettingChange("allow_retake", Boolean(checked))
                    }
                  >
                    Allow students to retake this quiz
                  </Checkbox>
                </Box>
              </Box>
            </SimpleGrid>

            <Box role="group" aria-label="Pace timer">
              <Text fontSize="sm" fontWeight="medium" mb={1.5}>
                Pace timer
              </Text>
              <Checkbox
                checked={settings.allow_student_timer ?? true}
                onCheckedChange={({ checked }) =>
                  onSettingChange("allow_student_timer", Boolean(checked))
                }
              >
                Allow students to use a personal pace timer
              </Checkbox>
              <Text fontSize="xs" color="fg.muted" mt={1}>
                An optional, student-controlled practice timer that never
                submits or changes the quiz time limit above.
              </Text>
            </Box>
          </SettingsSection>

          <SettingsSection
            title="Assignments & cohorts"
            description="Limit this quiz to specific programs or leave it open to everyone."
          >
            <ProgramSelector
              selectedPrograms={selectedPrograms}
              onChange={onProgramsChange}
            />
          </SettingsSection>

          <SettingsSection
            title="Tags"
            description="Add tags to organize and filter quizzes."
          >
            <TagInput
              tags={settings.tags ?? []}
              suggestions={recentTags}
              onChange={(newTags) => onSettingChange("tags", newTags)}
            />
          </SettingsSection>
        </VStack>

        <VStack gap={4} align="stretch">
          {buildTemplateSettingsPayload && onApplyTemplateSettings && (
            <SettingsSection
              title="Templates"
              description="Save or apply reusable quiz settings and tags."
            >
              <QuizTemplateControls
                currentSettings={settings}
                buildTemplateSettingsPayload={buildTemplateSettingsPayload}
                onApplyTemplateSettings={onApplyTemplateSettings}
              />
            </SettingsSection>
          )}

          <SettingsSection
            title="Results & review"
            description="Decide when results unlock and how much feedback students see."
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
              <Field label="Result visibility">
                <RadioGroup
                  colorPalette="blue"
                  value={settings.show_result_mode ?? "immediate"}
                  onValueChange={(event) =>
                    onSettingChange(
                      "show_result_mode",
                      (event.value as QuizSettings["show_result_mode"]) ??
                        "immediate",
                    )
                  }
                >
                  <VStack align="stretch" gap={2}>
                    <Radio value="immediate">Show immediately</Radio>
                    <Radio value="scheduled">Schedule results</Radio>
                  </VStack>
                </RadioGroup>
              </Field>

              <Field label="Rationale display">
                <RadioGroup
                  colorPalette="blue"
                  value={settings.show_rationale_mode ?? "after_all"}
                  onValueChange={(event) =>
                    onSettingChange(
                      "show_rationale_mode",
                      (event.value as QuizSettings["show_rationale_mode"]) ??
                        "after_all",
                    )
                  }
                >
                  <VStack align="stretch" gap={2}>
                    <Radio value="after_each" disabled={isScheduled}>
                      After each question
                      {isScheduled && (
                        <Text fontSize="xs" color="gray.500" ml={6}>
                          Not available with scheduled results
                        </Text>
                      )}
                    </Radio>
                    <Radio value="after_all">After quiz completion</Radio>
                    <Radio value="never">Do not show rationales</Radio>
                  </VStack>
                </RadioGroup>
              </Field>
            </SimpleGrid>

            {settings.show_result_mode === "scheduled" && (
              <Field label="Result release date">
                <Input
                  type="datetime-local"
                  value={settings.result_date ?? ""}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    onSettingChange("result_date", event.target.value || null)
                  }
                />
              </Field>
            )}

            <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
              <Checkbox
                checked={settings.show_score_comparison ?? false}
                onCheckedChange={({ checked }) => {
                  const nextValue = Boolean(checked)
                  onSettingChange("show_score_comparison", nextValue)
                  if (!nextValue && settings.show_percentile) {
                    onSettingChange("show_percentile", false)
                  }
                }}
              >
                Show score comparison
              </Checkbox>
              <Checkbox
                checked={settings.show_percentile ?? false}
                onCheckedChange={({ checked }) => {
                  const nextValue = Boolean(checked)
                  onSettingChange("show_percentile", nextValue)
                  if (nextValue) {
                    onSettingChange("show_score_comparison", true)
                  }
                }}
              >
                Show percentile rank to students
              </Checkbox>
              <Box>
                <Checkbox
                  checked={settings.allow_mpl_transmutation ?? false}
                  onCheckedChange={({ checked }) =>
                    onSettingChange("allow_mpl_transmutation", Boolean(checked))
                  }
                >
                  Allow MPL transmutation
                </Checkbox>
                <Text fontSize="xs" color="gray.600" mt={1} ml={6}>
                  Students can enter an MPL and view transmuted averages by tag.
                </Text>
                {settings.allow_mpl_transmutation && (
                  <Box mt={2} ml={6}>
                    <Checkbox
                      aria-label="Per-subject MPL"
                      checked={settings.mpl_per_subject ?? false}
                      onCheckedChange={({ checked }) =>
                        onSettingChange("mpl_per_subject", Boolean(checked))
                      }
                    >
                      Per-subject MPL
                    </Checkbox>
                    <Text fontSize="xs" color="gray.600" mt={1} ml={6}>
                      Shows per-subject score breakdowns with individual MPL
                      inputs (quizzes with multiple subjects only).
                    </Text>
                  </Box>
                )}
              </Box>
              <Box>
                <Checkbox
                  checked={settings.show_peer_performance ?? false}
                  onCheckedChange={({ checked }) =>
                    onSettingChange("show_peer_performance", Boolean(checked))
                  }
                  disabled={isRationaleDependentSettingDisabled}
                >
                  Show peer performance percentages
                </Checkbox>
                <Text fontSize="xs" color="gray.600" mt={1} ml={6}>
                  {isRationaleDependentSettingDisabled
                    ? "Enable rationales to allow students to compare answers."
                    : "Shows answer-choice percentages without raw totals."}
                </Text>
              </Box>
              <Box>
                <Checkbox
                  checked={settings.allow_search_without_attempt ?? false}
                  onCheckedChange={({ checked }) =>
                    onSettingChange(
                      "allow_search_without_attempt",
                      Boolean(checked),
                    )
                  }
                >
                  Make questions searchable without an attempt
                </Checkbox>
                <Text fontSize="xs" color="gray.600" mt={1} ml={6}>
                  Students can find these questions in search before taking the
                  quiz, while it is published and before its cutoff date.
                </Text>
              </Box>
              <Box>
                <Checkbox
                  checked={settings.allow_rationale_without_attempt ?? false}
                  onCheckedChange={({ checked }) =>
                    onSettingChange(
                      "allow_rationale_without_attempt",
                      Boolean(checked),
                    )
                  }
                  disabled={isRationaleDependentSettingDisabled}
                >
                  Show rationales without an attempt
                </Checkbox>
                <Text fontSize="xs" color="gray.600" mt={1} ml={6}>
                  {isRationaleDependentSettingDisabled
                    ? "Enable rationales to allow students to see them in search."
                    : "Students can read rationales in search before taking the quiz."}
                </Text>
              </Box>
            </SimpleGrid>
          </SettingsSection>
        </VStack>
      </SimpleGrid>
    </VStack>
  )
}

interface SettingsSectionProps {
  title: string
  description?: ReactNode
  children: ReactNode
}

function SettingsSection({
  title,
  description,
  children,
}: SettingsSectionProps) {
  return (
    <Box borderWidth="1px" borderRadius="md" p={4}>
      <Heading size="md">{title}</Heading>
      {description && (
        <Text color="gray.600" fontSize="sm" mt={2}>
          {description}
        </Text>
      )}
      <VStack gap={4} align="stretch" mt={4}>
        {children}
      </VStack>
    </Box>
  )
}
